import { Alert, Card, MoneyText, SkeletonRows } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  usePlatformHealth,
  usePlatformInvoices,
  usePlatformPermissions,
  useSupportGrants,
  useTenantOptions,
} from "../../features/platform/queries.js";
import { InvoiceStateBadge, PlatformPage } from "../../features/platform/shared.js";
import { errorMessage } from "../../lib/errors.js";
import { formatCount, formatDate } from "../../lib/format.js";

export const Route = createFileRoute("/platform/")({ component: PlatformOverview });

/**
 * Platform özeti (Prompt 24 web): kiracı sayıları, alacak (açık / gecikmiş), onay bekleyen destek istekleri ve sistem
 * sağlığının kısa görünümü. Her kart kendi izniyle görünür (finans rolü sağlığı, destek rolü alacakları görmez).
 */
function PlatformOverview(): ReactElement {
  const { t } = useTranslation();
  const permissions = usePlatformPermissions();
  const tenants = useTenantOptions();
  const canBilling = permissions.has("platform.billing.read");
  const canHealth = permissions.has("platform.health.read");
  const invoices = usePlatformInvoices({ tenantId: null, status: "OPEN" }, undefined, canBilling);
  const overdue = usePlatformInvoices({ tenantId: null, status: "OVERDUE" }, undefined, canBilling);
  const health = usePlatformHealth(canHealth);
  const pendingSupport = useSupportGrants({ status: "REQUESTED" });

  const items = tenants.data ?? [];
  const count = (status: string) => items.filter((tenant) => tenant.status === status).length;
  const stations = items.reduce((sum, tenant) => sum + tenant.activeStations, 0);

  return (
    <PlatformPage
      title={t("platform.overview.title")}
      description={t("platform.overview.description")}
      permission="platform.tenants.read"
    >
      {tenants.error ? <Alert tone="danger">{errorMessage(tenants.error)}</Alert> : null}
      <section aria-labelledby="platform-tenants-kpi" className="grid gap-3">
        <h2 id="platform-tenants-kpi" className="text-lg font-semibold">
          {t("platform.overview.tenants")}
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Kpi
            label={t("platform.overview.active")}
            value={formatCount(count("ACTIVE"))}
            loading={tenants.isPending}
          />
          <Kpi
            label={t("platform.overview.trial")}
            value={formatCount(count("TRIAL"))}
            loading={tenants.isPending}
          />
          <Kpi
            label={t("platform.overview.readOnly")}
            value={formatCount(count("READ_ONLY"))}
            loading={tenants.isPending}
            warn={count("READ_ONLY") > 0}
          />
          <Kpi
            label={t("platform.overview.suspended")}
            value={formatCount(count("SUSPENDED"))}
            loading={tenants.isPending}
            warn={count("SUSPENDED") > 0}
          />
          <Kpi
            label={t("platform.overview.stations")}
            value={formatCount(stations)}
            loading={tenants.isPending}
          />
          <Kpi
            label={t("platform.overview.pendingSupport")}
            value={formatCount(pendingSupport.data?.items.length ?? 0)}
            loading={pendingSupport.isPending}
            warn={(pendingSupport.data?.items.length ?? 0) > 0}
            to="/platform/destek"
          />
        </div>
      </section>

      {canBilling ? (
        <section aria-labelledby="platform-receivables" className="grid gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 id="platform-receivables" className="text-lg font-semibold">
              {t("platform.overview.receivables")}
            </h2>
            <Link
              to="/platform/faturalar"
              className="text-sm text-primary underline-offset-4 hover:underline"
            >
              {t("platform.overview.allInvoices")}
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi
              label={t("platform.overview.outstanding")}
              value={<MoneyText value={invoices.data?.totals.outstanding ?? "0"} />}
              loading={invoices.isPending}
            />
            <Kpi
              label={t("platform.overview.overdue")}
              value={<MoneyText value={overdue.data?.totals.overdue ?? "0"} />}
              loading={overdue.isPending}
              warn={(overdue.data?.items.length ?? 0) > 0}
            />
            <Kpi
              label={t("platform.overview.overdueInvoices")}
              value={formatCount(overdue.data?.items.length ?? 0)}
              loading={overdue.isPending}
            />
            <Kpi
              label={t("platform.overview.pastDueTenants")}
              value={formatCount(items.filter((tenant) => tenant.subscriptionStatus === "PAST_DUE").length)}
              loading={tenants.isPending}
            />
          </div>
          {(overdue.data?.items.length ?? 0) > 0 ? (
            <Card className="p-4">
              <h3 className="mb-2 text-sm font-semibold">{t("platform.overview.overdueList")}</h3>
              <ul className="grid gap-2">
                {overdue.data!.items.slice(0, 8).map((invoice) => (
                  <li key={invoice.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <Link
                      to="/platform/faturalar/$id"
                      params={{ id: invoice.id }}
                      className="font-medium text-primary underline-offset-4 hover:underline"
                    >
                      {invoice.tenant.name} · {invoice.invoiceNo}
                    </Link>
                    <span className="flex items-center gap-2">
                      <span className="text-muted-foreground">{formatDate(invoice.dueDate)}</span>
                      <MoneyText value={invoice.outstanding} />
                      <InvoiceStateBadge invoice={invoice} />
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </section>
      ) : null}

      {canHealth ? (
        <section aria-labelledby="platform-health-kpi" className="grid gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 id="platform-health-kpi" className="text-lg font-semibold">
              {t("platform.overview.health")}
            </h2>
            <Link to="/platform/saglik" className="text-sm text-primary underline-offset-4 hover:underline">
              {t("platform.overview.healthDetail")}
            </Link>
          </div>
          {health.error ? (
            <Alert tone="danger">{errorMessage(health.error)}</Alert>
          ) : (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Kpi
                label={t("platform.health.outboxPending")}
                value={formatCount(health.data?.outbox.pending ?? 0)}
                loading={health.isPending}
                warn={(health.data?.outbox.lagSeconds ?? 0) > 300}
              />
              <Kpi
                label={t("platform.health.failedJobs")}
                value={formatCount(health.data?.failedJobs.length ?? 0)}
                loading={health.isPending}
                warn={(health.data?.failedJobs.length ?? 0) > 0}
              />
              <Kpi
                label={t("platform.health.offlineAgents")}
                value={formatCount(health.data?.offlineAgents.total ?? 0)}
                loading={health.isPending}
                warn={(health.data?.offlineAgents.total ?? 0) > 0}
              />
              <Kpi
                label={t("platform.health.edocumentErrors")}
                value={formatCount(
                  (health.data?.edocumentErrors ?? []).reduce(
                    (sum, row) => sum + row.invoices + row.despatches,
                    0,
                  ),
                )}
                loading={health.isPending}
                warn={(health.data?.edocumentErrors.length ?? 0) > 0}
              />
            </div>
          )}
        </section>
      ) : null}
    </PlatformPage>
  );
}

function Kpi({
  label,
  value,
  loading,
  warn,
  to,
}: {
  label: string;
  value: ReactNode;
  loading: boolean;
  warn?: boolean;
  to?: "/platform/destek";
}): ReactElement {
  const body = (
    <>
      <p className="text-xs text-muted-foreground">{label}</p>
      {loading ? (
        <SkeletonRows rows={1} />
      ) : (
        <p className={`text-2xl font-semibold tabular ${warn ? "text-warning" : ""}`}>{value}</p>
      )}
    </>
  );
  return (
    <Card className={`p-4 ${warn ? "border-warning/50" : ""}`}>
      {to ? (
        <Link to={to} className="grid gap-1 rounded-sm">
          {body}
        </Link>
      ) : (
        <div className="grid gap-1">{body}</div>
      )}
    </Card>
  );
}
